import { API_ENDPOINT, getRecaptchaSiteKey } from "@/constants/appConstants";
import { useRegistrationProps } from "@/context/useRegistrationProps";
import { useRecaptchaV3 } from "@/hooks";
import { useNetwork } from "@/hooks/useNetwork";
import { Details } from "@/types/userDetails";
import { Button, OTPInput } from "@monsterindia/global-ui-components";
import React, { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";

interface FormValues {
  payload: Details;
  otp: string;
  otpId: string;
}

type OTPData = {
  otp: string;
  otpId: string;
};
interface VerifyOTPProps {
  payload: Details;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  closePopup: (type?: string) => void;
  onGoBackClickHandler: () => void;
  setOtpData: React.Dispatch<React.SetStateAction<OTPData>>;
}

export default function VerifyOTP({
  payload,
  setShowModal,
  closePopup,
  onGoBackClickHandler,
  setOtpData,
}: VerifyOTPProps) {
  const { fetchData } = useNetwork();
  const { translation, setDetails, setToast, handleEventTracking, hostname } =
    useRegistrationProps();
  const { socialLogin } = translation;
  const { executeRecaptcha, isReady: isRecaptchaReady } = useRecaptchaV3(
    getRecaptchaSiteKey(window.location.host),
    true,
  );

  const { handleSubmit, setValue, control } = useForm<FormValues>({
    defaultValues: {
      payload,
      otp: "",
      otpId: "",
    },
  });

  const otpValue = useWatch({ control, name: "otp" });

  useEffect(() => {
    if (isRecaptchaReady) {
      requestOTP();
    }
  }, [isRecaptchaReady]);

  const requestOTP = async () => {
    const { reCaptchaToken, reCaptchaAction } =
      await executeRecaptcha("claimNumber");
    try {
      const response = await fetchData(
        API_ENDPOINT(hostname).middleware.anonymousSendOtp,
        "POST",
        {},
        { ...payload, reCaptchaToken, reCaptchaAction },
      );
      const { id } = response.data as { id: string };
      if (id) setValue("otpId", id);
    } catch (error) {
      console.error("Error sending OTP:", error);
      setToast({ show: true, type: "error", message: "Failed to send OTP" });
    }
  };

  const verifyOTP = async (values: FormValues) => {
    try {
      const response = await fetchData(
        API_ENDPOINT(hostname).middleware.anonymousVerifyOtp,
        "POST",
        {},
        values,
      );
      const { status, message } = response.data as {
        status: string;
        message: string;
      };
      if (status === "VALID") {
        setShowModal(false);
        if (typeof window.MessageHandler?.postMessage === "function") {
          window.MessageHandler.postMessage(
            JSON.stringify({
              type: "CLAIM_NUMBER",
            }),
          );
        }
        setDetails((prevDetails: Details) => ({ ...prevDetails, ...values }));
        handleEventTracking({
          eventType: "eventPush",
          eventName: "Registration_Filled",
          eventProps: {
            Claim_Number_Added: `${values.payload.countryCode}${values.payload.mobileNumber}`,
          },
        });
        setOtpData({
          otp: values.otp,
          otpId: values.otpId,
        });
        closePopup("claimed_mobile_number");
      } else {
        setToast({ show: true, type: "error", message });
      }
    } catch (error) {
      setToast({ show: true, type: "error", message: "Something went wrong" });
    }
  };

  // const crossIcon = (
  //   <Cross onClick={closePopup} height={24} width={24} color="#777584" />
  // );

  return (
    <div className="flex h-full flex-col gap-4">
      <p className="text-content-primary rounded-[4px] bg-[#FFF5E5] p-2 text-sm">
        As this number is already linked to a different email in our system,
        please verify it's yours by entering the OTP to prevent misuse.
      </p>
      <div className="flex flex-col items-start justify-start gap-4">
        <p className="text-content-secondary text-base font-normal">
          We’ve sent an OTP to your number{" "}
          <span className="font-medium">
            +{payload.countryCode} {payload.mobileNumber}
          </span>
        </p>
        <OTPInput
          otpValue={otpValue}
          resendTime={30}
          otpUpdateHandler={(newOTP: string) => setValue("otp", newOTP)}
          resendClickHandler={() => {
            requestOTP();
            setValue("otp", "");
          }}
        />
      </div>

      <div className="mt-2 flex flex-col items-center justify-center gap-4">
        <Button
          disabled={otpValue?.length !== 6}
          type="submit"
          id="confirm-otp"
          label={socialLogin.confirm}
          onClick={handleSubmit(verifyOTP)}
          className="w-full"
          modifier="primary"
          size="lg"
        />
        <Button
          className="w-full"
          modifier="tertiary"
          size="lg"
          label="Go Back"
          onClick={() => onGoBackClickHandler()}
        />
      </div>
    </div>
  );
}
